import React, { useState } from 'react';

import { useFormikContext } from 'formik';

import { useManagementPackContext } from '../ManagementPackContext';
import { SNAPSHOT_COMPLETED } from '../managementPackUtils';

import CommentaryDraftPanel from './CommentaryDraftPanel';
import PageStrip from './PageStrip';
import SlideCanvas from './SlideCanvas';

import Alert from 'CommonComponents/Alert';
import IconButton from 'CommonComponents/IconButton';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import ChevronLeftIcon from 'Assets/icons/chevron-left.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';

/**
 * The pack as it will export: the selected slide, page navigation, and a
 * strip of every page.
 */
const PackPreview = () => {
    const { values } = useFormikContext();
    const { activePack, preview, previewHasPackCommentary, previewLoading, previewError, saveCommentary, options } = useManagementPackContext();

    const [requested, setRequested] = useState(0);

    const pages = preview?.pages || [];
    const current = Math.min(requested, Math.max(0, pages.length - 1));
    const page = pages[current];

    const isDraft = !activePack?.isIssued;
    const watermark = isDraft && !!values.options.watermark_drafts;
    // Commentary is editable only once the shown preview was built from the
    // completed snapshot: before that it may not carry the pack's stored (or
    // carried-forward) text, and a keystroke would save over it.
    const snapshotReady = activePack?.snapshotStatus === SNAPSHOT_COMPLETED;
    const commentaryEditable = previewHasPackCommentary && isDraft && !!values.settings.commentary_enabled && page?.commentary?.editable !== false;
    const showDraftPanel = commentaryEditable && !!page?.commentary?.primary && (!!page.commentary.draftable || (page.commentary.state || 'text') !== 'text');
    const showCommentaryHint = page?.commentary?.primary && isDraft && !snapshotReady && !!values.settings.commentary_enabled;

    return (
        <section className="tw-flex tw-flex-col tw-gap-4" aria-label={$t('Preview')}>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <div className="tw-flex tw-items-center tw-gap-2">
                    <h3 className="tw-text-base tw-font-semibold tw-text-charcoal tw-m-0">{$t('Preview')}</h3>
                    <span className="tw-text-sm tw-text-charcoal-75">
                        {activePack?.snapshotStatus === SNAPSHOT_COMPLETED
                            ? $t('Figures from the data captured for this pack')
                            : $t('Layout only; generate the pack to capture its figures')}
                    </span>
                    {previewLoading && <LoadingSpinner ySpacing={false} size="xs" />}
                </div>
                {pages.length > 0 && (
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <IconButton onClick={() => setRequested(current - 1)} disabled={current === 0} aria-label={$t('Previous page')} ghost>
                            <ChevronLeftIcon className="tw-w-4 tw-h-4 rtl:tw-rotate-180" aria-hidden="true" />
                        </IconButton>
                        <span className="tw-text-sm tw-text-charcoal-75" aria-live="polite">
                            {$t('Page {{current}} of {{total}}', { current: current + 1, total: pages.length })}
                        </span>
                        <IconButton onClick={() => setRequested(current + 1)} disabled={current >= pages.length - 1} aria-label={$t('Next page')} ghost>
                            <ChevronRightIcon className="tw-w-4 tw-h-4 rtl:tw-rotate-180" aria-hidden="true" />
                        </IconButton>
                    </div>
                )}
            </div>

            {previewError && (
                <Alert type="warning" role="status">
                    {previewError}
                </Alert>
            )}

            {preview?.overCap && (
                <Alert type="error">
                    {$t('This pack has {{count}} slides, more than the limit of {{max}}. Turn off some sections to export it.', {
                        count: preview.slideCount,
                        max: options.maxSlides,
                    })}
                </Alert>
            )}

            {pages.length > 1 && <PageStrip pages={pages} current={current} onSelect={setRequested} watermark={watermark} />}

            {page ? (
                <div className="tw-overflow-hidden tw-shadow-primary">
                    <SlideCanvas
                        page={page}
                        watermark={watermark}
                        commentaryEditable={commentaryEditable}
                        onSaveCommentary={saveCommentary}
                        label={$t('Page {{number}}: {{title}}', { number: current + 1, title: page.title || '' })}
                    />
                </div>
            ) : (
                <div className="tw-flex tw-items-center tw-justify-center tw-bg-white tw-aspect-video tw-text-sm tw-text-charcoal-75">
                    {previewLoading ? $t('Loading the preview') : $t('The preview appears here.')}
                </div>
            )}

            {showDraftPanel && <CommentaryDraftPanel key={page.commentary.pageKey} commentary={page.commentary} />}

            {showCommentaryHint && (
                <p className="tw-text-sm tw-text-charcoal-75 tw-m-0">
                    {activePack ? $t('Commentary can be edited once the pack data has been captured.') : $t('Generate the pack to add commentary.')}
                </p>
            )}
        </section>
    );
};

export default PackPreview;
